import { Typography } from "@mui/material";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { fileDownload } from "../helpers/files.ts";
import { TableRow } from "../layout/TableLayout/TableLayout.tsx";
import {
	ICatalogReport,
	ICatalogReportTableRow,
	IGenerationReport,
	IGenerationReportTableRow,
	IPartnerEarnings,
	ITopImages,
} from "../models/attribution-reports.ts";
import QueryService from "../utils/QueryService.ts";
import { showErrorToast } from "../utils/toast.tsx";
import useImageUtils from "../utils/useImageUtils.tsx";
import { useAppStore } from "./useStores.tsx";

export const useAttributionReports = () => {
	const imageQueryService = new QueryService("/images");
	const { pullUntilAvailable } = useImageUtils();
	const { t } = useTranslation("translation", { keyPrefix: "account.attributionReports" });
	const { attributionReportsStore, authStore } = useAppStore();
	const selectedReportMapper = {
		"Generation Report": {
			report: attributionReportsStore.generationReportData,
			isLoading: attributionReportsStore.isGenerationReportDataLoading,
		},
		"Catalog Report": {
			report: attributionReportsStore.catalogReportData,
			isLoading: attributionReportsStore.isCatalogReportDataLoading,
		},
		"Top images": {
			report: attributionReportsStore.topImagesData,
			isLoading: attributionReportsStore.isTopImagesDataLoading,
		},
		"Partners earnings": {
			report: attributionReportsStore.partnersEarningsData,
			isLoading: attributionReportsStore.isPartnersEarningsDataLoading,
		},
	};

	const createGenerationReportTableRow = (data: IGenerationReport): TableRow<IGenerationReportTableRow> => {
		return {
			rowStyle: {},
			data: {
				companyName: <Typography>{data.companyName}</Typography>,
				orgsNames: <Typography>{data.orgsNames}</Typography>,
				numberOfGenerations: <Typography>{data.numberOfGenerations}</Typography>,
			},
		};
	};

	const createCatalogReportTableRow = (data: ICatalogReport): TableRow<ICatalogReportTableRow> => {
		return {
			rowStyle: {},
			data: {
				partner: <Typography>{data.partner}</Typography>,
				imageTotal: <Typography>{data.imageTotal}</Typography>,
				imageTotalPercentage: <Typography>{data.imageTotalPercentage}%</Typography>,
				imagesTrained: <Typography>{data.imagesTrained}</Typography>,
				imagesTrainedPercentage: <Typography>{data.imagesTrainedPercentage}%</Typography>,
				imagesAttributed: <Typography>{data.imagesAttributed}</Typography>,
				imagesAttributedPercentage: <Typography>{data.imagesAttributedPercentage}%</Typography>,
				uniqueImagesAttributed: <Typography>{data.uniqueImagesAttributed}</Typography>,
				uniqueImagesAttributedPercentage: <Typography>{data.uniqueImagesAttributedPercentage}%</Typography>,
			},
		};
	};

	const createPartnersEarningsDataTableRow = (data: IPartnerEarnings) => {
		return {
			rowStyle: {},
			data: {
				partner: <Typography>{data.partner}</Typography>,
				totalPay: <Typography>${data.totalPay}</Typography>,
				payShare: <Typography>{data.payShare}%</Typography>,
			},
		};
	};

	const getSelectedReport = (): any[] => {
		if (!attributionReportsStore.selectedTab) return [];
		return selectedReportMapper[attributionReportsStore.selectedTab as keyof typeof selectedReportMapper].report;
	};

	const isSelectedReportLoading = (): boolean => {
		if (!attributionReportsStore.selectedTab) false;
		return selectedReportMapper[attributionReportsStore.selectedTab as keyof typeof selectedReportMapper].isLoading;
	};

	const exportReport = async (): Promise<void> => {
		attributionReportsStore.isExportReportLoading = true;
		try {
			const selectedReport: any[] = getSelectedReport();
			if (!selectedReport.length) {
				showErrorToast(t("currentReportIsEmpty"));
				return;
			}
			const workbook = XLSX.utils.book_new();
			const imageList: { imageId: string; imageUrl: string; filePath: string }[] = [];
			if (attributionReportsStore.selectedTab === "Top images") {
				const formatData = (key: "topHits" | "topPaid") => {
					return attributionReportsStore.topImagesData
						.map((row: ITopImages) => {
							const formattedRow: Record<string, any> = { Partner: row.partner };
							const valueLabel = key === "topHits" ? "hits" : "paid";
							// Map topHits or topPaid to columns
							(row[key] || []).forEach((item, index) => {
								formattedRow[`top_${index + 1}_id`] = item.id;
								formattedRow[`top_${index + 1}_url`] = item.url;
								formattedRow[`top_${index + 1}_${valueLabel}`] = item.value;
								imageList.push({
									imageId: item.id,
									imageUrl: item.url,
									filePath: `${row.partner}/${key}`,
								});
							});

							return formattedRow;
						})
						.filter((row) => Object.keys(row).length > 1);
				};
				const topHitsData = formatData("topHits");
				const topPaidData = formatData("topPaid");

				const topHitsSheet = XLSX.utils.json_to_sheet(topHitsData);
				XLSX.utils.book_append_sheet(workbook, topHitsSheet, "Top Hits");

				if (topPaidData.length) {
					const topPaidSheet = XLSX.utils.json_to_sheet(topPaidData);
					XLSX.utils.book_append_sheet(workbook, topPaidSheet, "Top Paid");
				}
			} else {
				//1. Main sheet
				// Exclude modelVersions column from the main sheet
				const mainSheetData = selectedReport.map(({ modelVersions, ...rest }) => rest);
				const worksheet = XLSX.utils.json_to_sheet(mainSheetData);
				XLSX.utils.book_append_sheet(workbook, worksheet, attributionReportsStore.selectedTab);
				if (attributionReportsStore.selectedTab === "Catalog Report") {
					let bria2CatalogData = attributionReportsStore.catalogReportBrai2Data;
					let bria3CatalogData = attributionReportsStore.catalogReportBrai3Data;

					if (bria2CatalogData.length > 0) {
						const mainSheet = XLSX.utils.json_to_sheet(bria2CatalogData);
						XLSX.utils.book_append_sheet(workbook, mainSheet, t("Bria2Models"));
					}

					if (bria3CatalogData.length > 0) {
						const bria2Sheet = XLSX.utils.json_to_sheet(bria3CatalogData);
						XLSX.utils.book_append_sheet(workbook, bria2Sheet, t("Bria3Models"));
					}
				} else if (attributionReportsStore.selectedTab === "Partners earnings") {
					let bria2PartnersEarningsData = attributionReportsStore.PartnersEarningsReportBrai2Data;
					let bria3PartnersEarningsData = attributionReportsStore.PartnersEarningsReportBrai3Data;

					if (bria2PartnersEarningsData.length > 0) {
						const mainSheet = XLSX.utils.json_to_sheet(bria2PartnersEarningsData);
						XLSX.utils.book_append_sheet(workbook, mainSheet, t("Bria2Models"));
					}

					if (bria3PartnersEarningsData.length > 0) {
						const bria2Sheet = XLSX.utils.json_to_sheet(bria3PartnersEarningsData);
						XLSX.utils.book_append_sheet(workbook, bria2Sheet, t("Bria3Models"));
					}
				} else {
					let generationReportBrai2Data = attributionReportsStore.generationReportBrai2Data;
					let generationReportBrai3Data = attributionReportsStore.generationReportBrai3Data;

					if (generationReportBrai2Data.length > 0) {
						const mainSheet = XLSX.utils.json_to_sheet(generationReportBrai2Data);
						XLSX.utils.book_append_sheet(workbook, mainSheet, t("Bria2Models"));
					}

					if (generationReportBrai3Data.length > 0) {
						const bria2Sheet = XLSX.utils.json_to_sheet(generationReportBrai3Data);
						XLSX.utils.book_append_sheet(workbook, bria2Sheet, t("Bria3Models"));
					}
				}
			}

			const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
			const excelBlob = new Blob([excelBuffer], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
			});
			const excelUrl = URL.createObjectURL(excelBlob);
			saveAs(excelUrl, `${attributionReportsStore.selectedTab}.xlsx`);

			if (authStore.user?.isSuperAdmin() && imageList.length > 0) {
				const data: any = await imageQueryService.post("/export_images_zip", {
					images: imageList.map((image) => ({
						file_name: `${image.imageId}`,
						file_path: image.filePath,
						image_url: image.imageUrl,
					})),
					output_prefix: attributionReportsStore.selectedTab,
				});
				await pullUntilAvailable(data.result_url, { maxAttempts: 600, sleepSecs: 5 });
				fileDownload(data.result_url);
			}
		} finally {
			attributionReportsStore.isExportReportLoading = false;
		}
	};

	return {
		createGenerationReportTableRow,
		createCatalogReportTableRow,
		createPartnersEarningsDataTableRow,
		exportReport,
		getSelectedReport,
		isSelectedReportLoading,
	};
};
